var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { Box, InputBase, styled } from '@mui/material';
import Typography from "@mui/material/Typography";
// Create a styled InputBase that visually matches Typography
var StyledInputBase = styled(InputBase, {
    shouldForwardProp: function (prop) { return prop !== 'typographyVariant' && prop !== 'showBorder' && prop !== 'useThemedBackground'; },
})(function (_a) {
    var _b, _c;
    var theme = _a.theme, typographyVariant = _a.typographyVariant, _d = _a.showBorder, showBorder = _d === void 0 ? true : _d, _e = _a.useThemedBackground, useThemedBackground = _e === void 0 ? false : _e, sx = _a.sx;
    // Get the typography styles for the specified variant
    var typographyStyles = typographyVariant ? theme.typography[typographyVariant] : {};
    return __assign(__assign({ padding: 0, margin: 0, fontFamily: (typographyStyles === null || typographyStyles === void 0 ? void 0 : typographyStyles.fontFamily) || theme.typography.fontFamily, fontSize: typographyStyles === null || typographyStyles === void 0 ? void 0 : typographyStyles.fontSize, fontWeight: typographyStyles === null || typographyStyles === void 0 ? void 0 : typographyStyles.fontWeight, lineHeight: typographyStyles === null || typographyStyles === void 0 ? void 0 : typographyStyles.lineHeight, letterSpacing: typographyStyles === null || typographyStyles === void 0 ? void 0 : typographyStyles.letterSpacing, textTransform: typographyStyles === null || typographyStyles === void 0 ? void 0 : typographyStyles.textTransform, transition: 'border-bottom 0.2s ease', borderBottom: '1px solid transparent' }, (showBorder && {
        '&:hover': {
            borderBottom: "1px solid ".concat((_c = (_b = theme.palette) === null || _b === void 0 ? void 0 : _b.primary) === null || _c === void 0 ? void 0 : _c.main),
        },
    })), { 
        // Remove default MUI Input styling
        '&::before, &::after': {
            display: 'none',
        }, '&:hover::before': {
            display: 'none',
        }, 
        // Style the input element to match typography
        '& .MuiInputBase-input': {
            padding: 0,
            fontFamily: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            lineHeight: 'inherit',
            letterSpacing: 'inherit',
            textTransform: 'inherit',
            textAlign: sx === null || sx === void 0 ? void 0 : sx.textAlign
        } });
});
var EditableTypography = forwardRef(function (props, ref) {
    var value = props.value, onChange = props.onChange, _a = props.variant, variant = _a === void 0 ? 'body1' : _a, color = props.color, type = props.type, _b = props.sx, sx = _b === void 0 ? {} : _b, _c = props.containerSx, containerSx = _c === void 0 ? {} : _c, _d = props.placeholder, placeholder = _d === void 0 ? 'Enter text...' : _d, _e = props.editingBackgroundColor, editingBackgroundColor = _e === void 0 ? undefined : _e, // 'rgba(255, 255, 255, 0.1)',
    _f = props.multiline, // 'rgba(255, 255, 255, 0.1)',
    multiline = _f === void 0 ? true : _f, suffix = props.suffix, _g = props.editable, editable = _g === void 0 ? true : _g, onKeyDown = props.onKeyDown, autoFocus = props.autoFocus, rows = props.rows, _h = props.showBorder, showBorder = _h === void 0 ? true : _h;
    var _j = useState(false), isEditing = _j[0], setIsEditing = _j[1];
    var _k = useState(false), isHovering = _k[0], setIsHovering = _k[1];
    var inputRef = useRef(null);
    var containerRef = useRef(null);
    // Use the forwarded ref if provided, otherwise use internal ref
    // @ts-ignore
    React.useImperativeHandle(ref, function () { return inputRef.current; });
    // Exit editing mode when clicking outside
    useEffect(function () {
        var handleClickOutside = function (event) {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsEditing(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return function () {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    // Handle hover states
    var handleMouseEnter = function () { return !isEditing && setIsHovering(true); };
    var handleMouseLeave = function () { return !isEditing && setIsHovering(false); };
    // Handle activation to edit mode
    var handleActivate = function () {
        setIsEditing(true);
        // Ensure input gets focus
        setTimeout(function () {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 0);
    };
    // Handle input changes
    var handleChange = function (e) {
        onChange(e.target.value);
    };
    // Handle input blur
    var handleBlur = function () {
        setIsEditing(false);
        setIsHovering(false);
    };
    return (_jsxs(Box, __assign({ ref: containerRef, sx: __assign({ position: 'relative', display: 'inline-block', minWidth: '1em', width: '100%' }, containerSx), onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave }, { children: [_jsx(StyledInputBase, { value: value, onChange: handleChange, inputRef: inputRef, placeholder: placeholder, sx: __assign(__assign({}, sx), { backgroundColor: isHovering || isEditing ? editingBackgroundColor : 'transparent', padding: '1px 3px', display: 'block', minHeight: '1.5em', width: '100%', cursor: 'pointer', paddingRight: suffix ? '0' : '0' }), color: color, fullWidth: true, autoFocus: autoFocus || isEditing, onBlur: handleBlur, onClick: function () { return setIsEditing(true); }, typographyVariant: variant, showBorder: showBorder, multiline: type != 'number' ? multiline : false, rows: rows, type: type, disabled: !editable, onKeyDown: onKeyDown, inputProps: props.inputProps }), suffix && (_jsx(Typography, __assign({ variant: variant, sx: {
                    position: 'absolute',
                    right: '2px',
                    top: '40%',
                    transform: 'translateY(-50%)',
                    color: 'text.secondary',
                    fontSize: 'inherit',
                    pointerEvents: 'none',
                    userSelect: 'none'
                } }, { children: suffix })))] })));
});
export default EditableTypography;
